import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { listGallery, Gallery, paramJumpPath } from '../../api/gallery';
import { absoluteURL, ApiError } from '../../api/request';
import Pagination from '../../components/Pagination';
import GalleryFilterBar from './GalleryFilterBar';
import { useGalleryFilter } from './useGalleryFilter';
import { useDictText } from '../../components/DictSelect';
import { useScopedDictText } from '../../utils/dictOptions';
import { thumbUrl } from '../../utils/imgUrl';

// 效果图 tab (2026-08-14 口径变更): 数据源 = 工艺参数行内嵌 effect_image 的实时聚合视图.
// 只读展示; 图片增删改走参数行编辑 (卡片点击直达来源参数行).
// - 过滤卡: 工艺架构 select + 设备/机型 select + 加工类型 select + 重置 + "共 N 张"
// - 3 列卡片网格: 缩略图 + 材料 + 设备·光源·工序 → 点击跳 ParamManage

const PAGE_SIZE = 24;

// 工艺架构 → 渐变, 与原型 SAMPLES 中的 s.g 风格对齐 (无真实图片时占位).
const ARCH_GRADIENT: Record<string, string> = {
    'laser-frame': 'linear-gradient(135deg,#d9b382,#a87b4a)',
    'laser-galvo': 'linear-gradient(135deg,#ffd700,#b8860b)',
    knife: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
    thermal: 'linear-gradient(135deg,#fca5a5,#ef4444)',
    embroidery: 'linear-gradient(135deg,#a7f3d0,#10b981)',
    print: 'linear-gradient(135deg,#bfdbfe,#3b82f6)',
};
const DEFAULT_GRADIENT = 'linear-gradient(135deg,#e2e8f0,#cbd5e1)';

function SampleList() {
    const history = useHistory();
    // 筛选状态 (含 ?xcode= 预填 / URL 状态还原) 与矩阵图 tab 共用 useGalleryFilter
    const f = useGalleryFilter('/gallery');
    const { query, page, setPage } = f;
    const [list, setList] = useState<Gallery[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 光源展示中文名 (字典 light_source), 卡片上不露 code
    const lightText = useDictText('light_source');
    // 加工类型 label (process_type, scope=archCode; 列表多框架混排逐个懒加载)
    const processTypeText = useScopedDictText(
        'process_type',
        list.map((s) => s.archCode),
    );

    // query 是每次渲染新建的对象, 序列化后做依赖避免 load 每帧重建
    const queryJson = JSON.stringify(query);
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listGallery({
                ...(JSON.parse(queryJson) as typeof query),
                hasMatrix: 'no', // 本 tab 只看普通效果图; 矩阵图在 /gallery/matrix
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [queryJson, page]);

    useEffect(() => {
        load();
    }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            {/* 顶部: 标题 + 导入按钮 */}
            <div>
                <div className="text-base font-semibold">效果图</div>
                <div className="text-xs text-slate-500">
                    工艺参数上的效果图 (effect_image) 聚合视图 · 按 工艺架构 /
                    设备 / 加工类型 检索 · 编辑请到来源参数行
                </div>
            </div>

            <GalleryFilterBar f={f} total={total} />

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {/* 3 列卡片网格 */}
            {loading ? (
                <div className="text-center text-slate-400 text-sm py-10">
                    加载中…
                </div>
            ) : list.length === 0 ? (
                <div className="text-slate-400 text-sm text-center py-10">
                    该筛选下暂无效果图
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-4">
                    {list.map((s) => {
                        const gradient =
                            ARCH_GRADIENT[s.archCode] || DEFAULT_GRADIENT;
                        return (
                            <div
                                key={`${s.archCode}-${s.id}`}
                                className="card overflow-hidden group relative cursor-pointer hover:border-brand-500 hover:shadow-sm"
                                onClick={() =>
                                    history.push(
                                        `/gallery/asset/${encodeURIComponent(s.archCode)}/${s.id}?kind=effect`,
                                    )
                                }
                            >
                                {s.imageUrl ? (
                                    <img
                                        src={thumbUrl(absoluteURL(s.imageUrl))}
                                        alt={s.xcode || `gallery-${s.id}`}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            height: 130,
                                            objectFit: 'cover',
                                        }}
                                        loading="lazy"
                                    />
                                ) : (
                                    <div
                                        style={{
                                            height: 130,
                                            background: gradient,
                                        }}
                                    />
                                )}
                                <div className="p-3 space-y-1">
                                    <div className="text-sm font-semibold">
                                        {s.xcode ? (
                                            <PermissionLink
                                                resource="material"
                                                to={`/materials/${encodeURIComponent(s.xcode)}`}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                title="查看材料详情"
                                                className="text-brand-600 hover:underline font-mono"
                                            >
                                                {s.xcode}
                                            </PermissionLink>
                                        ) : (
                                            '未指定材料'
                                        )}
                                    </div>
                                    <div
                                        className="text-xs text-slate-500"
                                        title={s.deviceKey || ''}
                                    >
                                        {f.devices.find(
                                            (d) => d.deviceKey === s.deviceKey,
                                        )?.name || s.deviceKey}{' '}
                                        · {lightText(s.lightSource)} ·{' '}
                                        {processTypeText(
                                            s.archCode,
                                            s.processType,
                                        )}
                                    </div>
                                    <div className="flex items-center justify-end pt-1">
                                        <Link
                                            to={paramJumpPath(s)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-[11px] text-brand-600 hover:underline"
                                        >
                                            工艺参数 →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex items-center justify-end text-xs text-slate-500">
                <div className="flex items-center gap-2">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>
        </div>
    );
}

export default SampleList;
